const CALENDLY_STYLESHEET_ID = "calendly-widget-stylesheet";
const CALENDLY_SCRIPT_ID = "calendly-widget-script";
const CALENDLY_STYLESHEET_URL = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_SCRIPT_URL = "https://assets.calendly.com/assets/external/widget.js";

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: any) => void;
    };
  }
}

let assetsPromise: Promise<void> | undefined;

function ensureStylesheet(): Promise<void> {
  const existing = document.getElementById(CALENDLY_STYLESHEET_ID) as HTMLLinkElement | null;
  if (existing?.sheet) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const link = existing ?? document.createElement("link");

    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => reject(new Error("Failed to load Calendly stylesheet")), { once: true });

    if (!existing) {
      link.id = CALENDLY_STYLESHEET_ID;
      link.rel = "stylesheet";
      link.href = CALENDLY_STYLESHEET_URL;
      document.head.appendChild(link);
    }
  });
}

function ensureScript(): Promise<void> {
  if (window.Calendly?.initPopupWidget) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(CALENDLY_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");

    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Failed to load Calendly script")), { once: true });

    if (!existing) {
      script.id = CALENDLY_SCRIPT_ID;
      script.src = CALENDLY_SCRIPT_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

/**
 * Load Calendly's assets once per page. The shared promise prevents concurrent
 * Button and ContentLayout instances from injecting duplicate elements.
 */
export function ensureCalendlyAssets(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();

  if (!assetsPromise) {
    assetsPromise = Promise.all([ensureStylesheet(), ensureScript()])
      .then(() => undefined)
      .catch((error) => {
        assetsPromise = undefined;
        throw error;
      });
  }

  return assetsPromise;
}
